import React, { useCallback, useState } from "react";
import { QuizFrame } from "./QuizShell";
import { TypeQuiz, TypeQuizProps } from "./TypeQuiz";
import { PropertyQuiz, PropertyQuizProps } from "./PropertyQuiz";
import { ServiceQuiz, ServiceQuizProps } from "./ServiceQuiz";
import { TemplateQuiz, TemplateQuizProps } from "./TemplateQuiz";
import { AudienceQuiz, AudienceQuizProps } from "./AudienceQuiz";
import { ScheduleQuiz, ScheduleQuizProps } from "./ScheduleQuiz";

type Omitted = "progress" | "onTotal";

export interface ConfigQuizProps {
  currentStep: number;
  campaignType: "property" | "service" | null;
  isMultipleTemplateMode: boolean;
  isMultipleServiceTemplateMode: boolean;
  /** Renders the classic screen for the current step (used where a quiz screen would lose features). */
  renderClassic: () => React.ReactNode;
  /** Mount hidden classic step content so its dialogs (upgrade, save-draft) can open. */
  needsClassicDialogs: boolean;
  onExit: () => void;
  onSwitchToClassic: () => void;
  /** Navigate between steps with the builder's own guards. */
  goToStep: (n: number) => void;
  type: Omit<TypeQuizProps, "progress">;
  property: Omit<PropertyQuizProps, Omitted | "onExit">;
  service: Omit<ServiceQuizProps, Omitted | "onExit">;
  template: Omit<TemplateQuizProps, "progress" | "campaignType" | "onBack">;
  audience: Omit<AudienceQuizProps, Omitted | "onBack">;
  schedule: Omit<ScheduleQuizProps, Omitted | "onBack">;
}

/**
 * Guided "quiz" front-end for the configuration part of the launch builder
 * (steps 1–5). It only renders and collects answers — all state, saving,
 * billing and navigation rules stay in CampaignBuilder.
 *
 * Step 4 (live template preview/edit/proof) and steps 6–9 (schedule, payment,
 * launch) reuse the classic screens inside the quiz frame.
 */
export const ConfigQuiz: React.FC<ConfigQuizProps> = (props) => {
  const { currentStep, campaignType } = props;
  // Number of questions reported by the variable-length sub-quizzes.
  const [t2, setT2] = useState(8);
  const [t5, setT5] = useState(3);
  const [t6, setT6] = useState(3);
  const onTotal2 = useCallback((n: number) => setT2((p) => (p === n ? p : n)), []);
  const onTotal5 = useCallback((n: number) => setT5((p) => (p === n ? p : n)), []);
  const onTotal6 = useCallback((n: number) => setT6((p) => (p === n ? p : n)), []);

  // 1 (type) + t2 (details) + template + preview + t5 (audience) + t6 (schedule)
  const total = 1 + t2 + 1 + 1 + t5 + t6;

  let content: React.ReactNode = null;
  let position = total;
  let embedded = false;

  const multiProperty = campaignType === "property" && props.isMultipleTemplateMode;
  const multiService = campaignType === "service" && props.isMultipleServiceTemplateMode;

  switch (currentStep) {
    case 1:
      position = 1;
      content = <TypeQuiz {...props.type} progress={{ before: 0, after: total - 1 }} />;
      break;

    case 2:
      if (campaignType === "property" && !multiProperty) {
        content = (
          <PropertyQuiz
            {...props.property}
            onTotal={onTotal2}
            onExit={() => props.goToStep(1)}
            progress={{ before: 1, after: total - 1 - t2 }}
          />
        );
        position = 2;
      } else if (campaignType === "service" && !multiService) {
        content = (
          <ServiceQuiz
            {...props.service}
            onTotal={onTotal2}
            onExit={() => props.goToStep(1)}
            progress={{ before: 1, after: total - 1 - t2 }}
          />
        );
        position = 2;
      } else {
        embedded = true;
        position = 2;
        content = props.renderClassic();
      }
      break;

    case 3:
      if (campaignType && !multiProperty && !multiService) {
        content = (
          <TemplateQuiz
            {...props.template}
            campaignType={campaignType}
            onBack={() => props.goToStep(2)}
            progress={{ before: 1 + t2, after: total - 1 - t2 - 1 }}
          />
        );
        position = 1 + t2 + 1;
      } else {
        embedded = true;
        position = 1 + t2 + 1;
        content = props.renderClassic();
      }
      break;

    case 4:
      embedded = true;
      position = 1 + t2 + 2;
      content = props.renderClassic();
      break;

    case 5:
      content = (
        <>
          <AudienceQuiz
            {...props.audience}
            onTotal={onTotal5}
            onBack={() => props.goToStep(4)}
            progress={{ before: 1 + t2 + 2, after: t6 }}
          />
          {props.needsClassicDialogs && (
            <div className="hidden" aria-hidden>
              {props.renderClassic()}
            </div>
          )}
        </>
      );
      position = 1 + t2 + 3;
      break;

    case 6:
      content = (
        <>
          <ScheduleQuiz
            {...props.schedule}
            onTotal={onTotal6}
            onBack={() => props.goToStep(5)}
            progress={{ before: 1 + t2 + 2 + t5, after: 0 }}
          />
          {props.needsClassicDialogs && (
            <div className="hidden" aria-hidden>
              {props.renderClassic()}
            </div>
          )}
        </>
      );
      position = 1 + t2 + 2 + t5 + 1;
      break;

    default:
      embedded = true;
      position = total;
      content = props.renderClassic();
  }

  return (
    <QuizFrame
      position={Math.min(position, total)}
      total={total}
      onExit={props.onExit}
      onSwitchToClassic={props.onSwitchToClassic}
      wide={embedded}
    >
      {embedded ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-6">
          {content}
        </div>
      ) : (
        content
      )}
    </QuizFrame>
  );
};
